import { createFileRoute } from "@tanstack/react-router";
import {
  LiveChart,
  LiveRatesTable,
  HindiWeightCalc,
  InHiLinkMatrix,
  FaqAccordion,
  SilverStrip,
  buildInHiSchema,
  HindiUniqueBlock,
  hiDateParts,
  goldRatesQueryOptions,
} from "@/components/in-hi/shell";
import { useGoldRates } from "@/lib/nonblocking-rates";
import { computeCityPrices, formatMoney } from "@/lib/gold-data";

const SLUG = "/in/hi/gold-weight-per-kilo";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "सोना कितने रुपए किलो है आज का ताजा रेट?", a: "सोने का भाव मुख्य रूप से प्रति 10 ग्राम (तोला) के हिसाब से देखा जाता है। 1 किलोग्राम सोने का भाव निकालने के लिए आज के प्रति ग्राम रेट को 1000 से गुणा किया जाता है, जिसकी लाइव गणना हमारे कैलकुलेटर सेक्शन में उपलब्ध है।" },
  { q: "पुराना सोना या चांदी बेचने पर ज्वेलर्स कितना पैसा काटते हैं?", a: "अधिकांश ज्वेलर पुराने आभूषण की खरीद पर आज के कच्चे सोने के दाम से 10–15% की कटौती करते हैं — इसमें रिफाइनिंग लॉस, टेस्टिंग शुल्क और डीलर मार्जिन शामिल है। हॉलमार्किंग वाले 22K/24K पर कटौती 8–10% तक भी संभव है।" },
  { q: "1 तोला सोना आज कितने रुपए का है?", a: "1 तोला = 11.6638 ग्राम। तोला भाव निकालने के लिए आज के प्रति ग्राम रेट को 11.66 से गुणा करें। ऊपर लाइव तालिका में तोला कॉलम में यह गणना अपने आप हो जाती है।" },
];

export const Route = createFileRoute("/in/hi/gold-weight-per-kilo")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const dp = hiDateParts();
    const title = `सोना कितने रुपए किलो है आज — प्रति किलोग्राम लाइव भाव`;
    const description = `1 किलोग्राम शुद्ध 24K और 22K सोने का आज का लाइव भाव भारत में — साथ ही तोला, ग्राम व 10 ग्राम की तुलना। थोक और निवेश खरीदारों के लिए बुलियन कैलकुलेटर।`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "hi-IN" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "hi_IN" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "hi-IN", href: CANONICAL },
      ],
      scripts: [{ type: "application/ld+json", children: JSON.stringify(buildInHiSchema({ slug: SLUG, name: title, description, faqs: FAQS, breadcrumbLabel: "1 किलो सोना — थोक/बुलियन भाव" })) }],
    };
  },
  component: Page,
});

function Page() {
  const dp = hiDateParts();
  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">भारत · बुलियन/थोक</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          सोना कितने रुपए किलो है — 1 किलोग्राम शुद्ध सोने का आज का भाव ({dp.dayNum} {dp.month} {dp.year})
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          निवेशकों और थोक खरीदारों के लिए <strong>1 किलोग्राम 24K सोने का लाइव भाव</strong> — प्रति ग्राम रेट × 1000 के सीधे फॉर्मूले से निकाला गया। साथ ही तोला, 10 ग्राम और ग्राम की तुलना नीचे तालिका में।
        </p>
      </header>
      <HindiUniqueBlock slug={SLUG} />
      <LiveChart title={`प्रति किलो सोना ट्रेंड — ${dp.month} ${dp.year}`} />
      <LiveRatesTable />
      <BulkWeightMatrix />
      <HindiWeightCalc />
      <SilverStrip />
      <FaqAccordion items={FAQS} />
      <InHiLinkMatrix current={SLUG} />
    </main>
  );
}

function BulkWeightMatrix() {
  const { data } = useGoldRates();
  const p = computeCityPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: data.inrPerUsd, country: "india", cityPremium: 1 });
  const rows = [
    { label: "1 ग्राम", g: 1 },
    { label: "1 तोला (11.66 g)", g: 11.6638 },
    { label: "10 ग्राम", g: 10 },
    { label: "100 ग्राम", g: 100 },
    { label: "500 ग्राम", g: 500 },
    { label: "1 किलो (1000 g)", g: 1000 },
  ];
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <h2 className="text-xl md:text-2xl font-bold mb-3">वजन के हिसाब से आज सोने की कीमत — 24K vs 22K</h2>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">वजन</th>
              <th className="px-3 py-2 text-right">24 कैरेट</th>
              <th className="px-3 py-2 text-right">22 कैरेट</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-amber-500/10">
                <th className="px-3 py-2 text-left font-medium">{r.label}</th>
                <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-300">{formatMoney(p.perGram24 * r.g, "INR")}</td>
                <td className="px-3 py-2 text-right">{formatMoney(p.perGram22 * r.g, "INR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}